import React, {
  useState, useEffect, useRef, useMemo, useCallback,
} from 'react';
import { PropTypes } from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { getTagInfo } from '../../utils/api/tag';
import { withContextProvider } from '../../hoc';
import { DialogsProvider, ToastProvider } from '../../providers';
import Site from '../../componentsV2/Sites/Site';
import { useToasts } from '../../hooks';
import Breadcrumbs from '../../componentsV2/Breadcrumbs/Breadcrumbs';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';
import TagModal from '../../componentsV2/Tags/TagsTabbedModal';
import Button from '../../componentsV2/Button/Button';
import { useUrlModals } from '../../utils/useUrlModals';
import { fetchTagsForSites } from '../../utils/tagUtils';
import Pagination from '../../componentsV2/pagination/Pagination';
import ResultCount from '../../componentsV2/ResultCount/ResultCount';
import SitesCardSkeletonLoader from '../../componentsV2/LoadingSkeletons/SitesCardSkeletonLoader';

const TagShow = ({ tagId, organizationId }) => {
  const [tagData, setTagData] = useState({});
  const [tagSites, setTagSites] = useState([]);
  const [totalSites, setTotalSites] = useState(0);
  const [allTagSites, setAllTagSites] = useState({});
  const [selectedSitesTags, setSelectedSitesTags] = useState([]);
  const [tagSitesBtnDisabled, setTagSitesBtnDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const { showToast } = useToasts();
  const selectAllRef = useRef();
  const loadingTimeoutRef = useRef();
  const tagBtnTimeoutRef = useRef();

  const ITEMS_PER_PAGE = 24;

  const parseQueryParams = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const rawPerPage = parseInt(urlParams.get('per_page'), 10);
    const perPage = Number.isNaN(rawPerPage) || rawPerPage < 1 ? ITEMS_PER_PAGE : rawPerPage;
    const rawPage = parseInt(urlParams.get('page'), 10);

    return {
      page: Number.isNaN(rawPage) || rawPage < 1 ? 1 : rawPage,
      per_page: perPage,
    };
  }, [ITEMS_PER_PAGE]);

  const [searchParams, setSearchParams] = useState(parseQueryParams);

  // useUrlModals for tagSitesModal (deep-linking)
  const { modals, openModal, closeModal } = useUrlModals({ tagSitesModal: {} });
  const tagSitesModal = modals.tagSitesModal || { isOpen: false, value: null };

  // Centralized function to update URL and trigger fetch
  const updateSearchParamsAndUrl = useCallback((params) => {
    const newURL = new URLSearchParams(params).toString();
    window.history.pushState({}, '', `?${newURL}`);
    setSearchParams(parseQueryParams());
  }, [parseQueryParams]);

  useEffect(() => {
    setIsLoading(true);

    getTagInfo(tagId, organizationId, {
      page: searchParams.page,
      per_page: searchParams.per_page,
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.tag && !data.sites) {
          throw new Error(data.error);
        }
        setTagData(data.tag);
        setTagSites(data.sites || []);
        setTotalSites(data.total_count || data.sites_count || data.sites?.length || 0);
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      })
      .finally(() => {
        loadingTimeoutRef.current = setTimeout(() => setIsLoading(false), 200);
      });

    return () => clearTimeout(loadingTimeoutRef.current);
  }, [tagId, organizationId, searchParams.page, searchParams.per_page, showToast]);

  const initialAllTagSites = useMemo(
    () => tagSites.reduce((acc, site) => ({ ...acc, [site.id]: false }), {}),
    [tagSites],
  );

  useEffect(() => {
    setAllTagSites(initialAllTagSites);
    setTagSitesBtnDisabled(true);
    if (selectAllRef.current) {
      selectAllRef.current.checked = false;
      selectAllRef.current.indeterminate = false;
      selectAllRef.current.style.appearance = 'none';
    }
  }, [initialAllTagSites]);

  const breadcrumbLinks = [{ href: '/tags', name: 'Tags' }, { name: tagData.name || 'Tag' }];

  const handlePageChange = (page) => {
    if (page < 1 || page > Math.ceil(totalSites / searchParams.per_page)) return;

    const updatedParams = {
      page,
      per_page: searchParams.per_page,
    };

    updateSearchParamsAndUrl(updatedParams);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setSearchParams(parseQueryParams());
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [parseQueryParams]);

  // Prepares data and UI state when the Tag Sites modal is opened
  const prepareTagsModal = async () => {
    try {
      setTagSitesBtnDisabled(true);
      setSelectedSitesTags([]);

      const selectedSiteKeys = Object.keys(allTagSites).filter((key) => allTagSites[key]);

      const tagAndSites = await fetchTagsForSites(selectedSiteKeys, organizationId, showToast);
      setSelectedSitesTags(tagAndSites);
    } catch (error) {
      setTagSitesBtnDisabled(false);
      showToast({ message: error.message, type: 'error' });
    }
  };

  const handleOpenTagsModal = () => {
    openModal('tagSitesModal', true);
  };

  const handleClose = (isCloseOnCreate) => {
    closeModal('tagSitesModal');
    setSelectedSitesTags([]);
    if (isCloseOnCreate) {
      setAllTagSites(initialAllTagSites);
      if (selectAllRef.current) {
        selectAllRef.current.checked = false;
        selectAllRef.current.style.appearance = 'none';
      }
    }
    clearTimeout(tagBtnTimeoutRef.current);
    tagBtnTimeoutRef.current = setTimeout(() => setTagSitesBtnDisabled(false), 3000);
  };

  useEffect(() => () => clearTimeout(tagBtnTimeoutRef.current), []);

  // If modal was opened via URL, prepare the modal data
  useEffect(() => {
    if (tagSitesModal.isOpen) {
      prepareTagsModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagSitesModal.isOpen]);

  const handleSelectAllCheck = () => {
    if (selectAllRef.current.checked) {
      setTagSitesBtnDisabled(false);
      selectAllRef.current.style.appearance = 'auto';
      const allTagSitesTemp = Object.keys(allTagSites).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {},
      );
      setAllTagSites({ ...allTagSitesTemp });
    } else {
      setTagSitesBtnDisabled(true);
      selectAllRef.current.style.appearance = 'none';
      const allTagSitesTemp = Object.keys(allTagSites).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {},
      );
      setAllTagSites({ ...allTagSitesTemp });
    }
  };

  // Render skeleton loader
  const renderSkeletonLoader = () => {
    const loaderKeys = Array.from({ length: searchParams.per_page }, () => uuidv4());
    return (
      <div className="grid justify-start grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 3xl:grid-cols-6 gap-x-6 gap-y-8">
        {loaderKeys.map((key) => (
          <SitesCardSkeletonLoader key={key} />
        ))}
      </div>
    );
  };

  return (
    <DialogsProvider>
      <div className="ml-2.75">
        <header>
          <Breadcrumbs links={breadcrumbLinks} />
        </header>

        <div className="flex w-full justify-between items-center pb-4">
          <PagesHeader name="tags" label={`${tagData.name || 'Tag'} Sites`} size="h-9" />

          {!isLoading && totalSites > 0 && (
            <ResultCount
              offset={(searchParams.page - 1) * searchParams.per_page}
              pageSize={tagSites.length}
              total={totalSites}
              unfilteredTotal={totalSites}
              viewType="gallery"
              pageType="sites"
            />
          )}

          <div className="flex justify-start items-center gap-4">
            <label htmlFor="selectAllInput" className="leading-[20.83px] w-[165px]">
              <input
                id="selectAllInput"
                type="checkbox"
                className="appearance-none border border-vamos-gray bg-vamos-v-light-gray w-[21px] h-[21px] rounded-[5px]"
                ref={selectAllRef}
                onChange={handleSelectAllCheck}
                disabled={isLoading || tagSites.length === 0}
              />
              <span className="relative -top-1 ml-2.5">
                Select all on page
              </span>
            </label>
            <Button
              label="Add/remove tags"
              color="primary"
              onClick={handleOpenTagsModal}
              isDisabled={tagSitesBtnDisabled || isLoading}
            />
          </div>
        </div>

        {isLoading ? (
          renderSkeletonLoader()
        ) : (
          <>
            {tagSites.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No sites found for this tag.
              </div>
            ) : (
              <div className="grid justify-start grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 3xl:grid-cols-6 gap-x-6 gap-y-8">
                {tagSites.map((site) => (
                  <Site
                    site={site}
                    key={site.id}
                    allTagSites={allTagSites}
                    setAllTagSites={setAllTagSites}
                    selectAllRef={selectAllRef}
                    sourcePage="tagShow"
                    setTagSitesBtnDisabled={setTagSitesBtnDisabled}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Pagination - only show if more than one page */}
        {!isLoading && totalSites > searchParams.per_page && (
          <footer className="mt-8">
            <Pagination
              itemsPerPage={searchParams.per_page}
              totalItems={totalSites}
              currentPage={searchParams.page}
              setCurrentPage={handlePageChange}
            />
          </footer>
        )}

        <div className="flex flex-col py-5">
          {tagSitesModal.isOpen && (
            <TagModal
              handleClose={handleClose}
              siteIds={Object.keys(allTagSites)
                .filter((key) => allTagSites[key] === true)
                .map(Number)}
              orgId={organizationId}
              title="Tags"
              isTagAsTabShowing
              isCreateNewTabShowing
              existingTags={selectedSitesTags}
              isTagShowPage={tagId}
              isAllSelected={selectAllRef.current?.checked}
            />
          )}
        </div>
      </div>
    </DialogsProvider>
  );
};

export default withContextProvider(ToastProvider)(TagShow);

TagShow.propTypes = {
  organizationId: PropTypes.number.isRequired,
  tagId: PropTypes.number.isRequired,
};
